import React, { useEffect, useMemo, useState } from "react";
import AdminLayout from "../../components/layouts/AdminLayout";
import { Mail, Archive, Eye, Trash2, Loader2, Inbox } from "lucide-react";
import { collection, deleteDoc, doc, onSnapshot, orderBy, query, updateDoc } from "firebase/firestore";
import { firestore } from "../../firebaseConfig";
import toast from "react-hot-toast";

const SENTIMENT_ENDPOINT = process.env.REACT_APP_FUNCTIONS_URL
  ? `${process.env.REACT_APP_FUNCTIONS_URL}/sentiment`
  : null;

const StatusPill = ({ status }) => {
  const styles = useMemo(() => {
    switch (status) {
      case "new":
        return "bg-blue-50 text-blue-700";
      case "archived":
        return "bg-slate-100 text-slate-700";
      default:
        return "bg-amber-50 text-amber-700";
    }
  }, [status]);

  return <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${styles}`}>{status || "new"}</span>;
};

const AdminMessages = () => {
  const [messages, setMessages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sentiment, setSentiment] = useState(null);

  useEffect(() => {
    if (!firestore) return;
    const q = query(collection(firestore, "visitorMessages"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = [];
        snap.forEach((d) => rows.push({ id: d.id, ...d.data() }));
        setMessages(rows);
      },
      (err) => console.error("visitorMessages listen failed", err)
    );
    return () => unsub();
  }, []);

  const handleSelect = async (msg) => {
    setSelected(msg);
    setSentiment(null);
    if (!SENTIMENT_ENDPOINT || !msg?.message) return;
    try {
      const res = await fetch(SENTIMENT_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: msg.message }),
      });
      const json = await res.json();
      setSentiment(json?.result || json?.data?.result || json?.data || null);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("sentiment fetch failed", err);
    }
  };

  const updateStatus = async (msg, status) => {
    if (!firestore || !msg?.id) return;
    setLoading(true);
    try {
      await updateDoc(doc(firestore, "visitorMessages", msg.id), { status });
      toast.success(`Message ${status}`);
    } catch (err) {
      toast.error("Failed to update status");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (msg) => {
    if (!firestore || !msg?.id) return;
    const confirmed = window.confirm("Delete this message permanently?");
    if (!confirmed) return;
    setLoading(true);
    try {
      await deleteDoc(doc(firestore, "visitorMessages", msg.id));
      toast.success("Message deleted");
      if (selected?.id === msg.id) {
        setSelected(null);
        setSentiment(null);
      }
    } catch (err) {
      toast.error("Failed to delete");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <header className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-purple-50 text-purple-700 flex items-center justify-center border border-purple-100">
            <Mail className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-slate-900">Landing Messages</h1>
            <p className="text-sm text-slate-600">
              Review, archive, or delete messages submitted from the public landing page. Click a row to view details.
            </p>
          </div>
        </header>

        <div className="grid gap-4 lg:grid-cols-3">
          <div className="lg:col-span-2 bg-white border border-slate-100 rounded-xl shadow-sm overflow-hidden">
            <table className="min-w-full divide-y divide-slate-100">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">From</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Subject</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {messages.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-6 text-center text-sm text-slate-500">
                      <div className="flex items-center justify-center gap-2 text-slate-500">
                        <Inbox className="h-4 w-4" />
                        No messages yet
                      </div>
                    </td>
                  </tr>
                )}
                {messages.map((message) => (
                  <tr
                    key={message.id}
                    className="hover:bg-slate-50 cursor-pointer"
                    onClick={() => handleSelect(message)}
                  >
                    <td className="px-4 py-3 text-sm text-slate-800">{message.name || "Unknown"}</td>
                    <td className="px-4 py-3 text-sm text-slate-700">{message.email || "—"}</td>
                    <td className="px-4 py-3 text-sm text-slate-700">{message.subject || "No subject"}</td>
                    <td className="px-4 py-3">
                      <StatusPill status={message.status || "new"} />
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700 flex items-center gap-2">
                      <button
                        className="px-2 py-1 text-xs font-semibold text-blue-700 hover:underline inline-flex items-center gap-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelect(message);
                        }}
                      >
                        <Eye className="h-3.5 w-3.5" />
                        View
                      </button>
                      <button
                        className="px-2 py-1 text-xs font-semibold text-slate-700 hover:underline inline-flex items-center gap-1"
                        disabled={loading}
                        onClick={(e) => {
                          e.stopPropagation();
                          updateStatus(message, message.status === "archived" ? "new" : "archived");
                        }}
                      >
                        {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Archive className="h-3.5 w-3.5" />}
                        {message.status === "archived" ? "Unarchive" : "Archive"}
                      </button>
                      <button
                        className="px-2 py-1 text-xs font-semibold text-red-700 hover:underline inline-flex items-center gap-1"
                        disabled={loading}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(message);
                        }}
                      >
                        {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white border border-slate-100 rounded-xl shadow-sm p-4">
            <h2 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <Eye className="h-4 w-4" />
              Message detail
            </h2>
            {selected ? (
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">From</span>
                  <span className="font-medium text-slate-800">{selected.name || "Unknown"}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Email</span>
                  <span className="font-medium text-slate-800">{selected.email || "—"}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Subject</span>
                  <span className="font-medium text-slate-800">{selected.subject || "No subject"}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Status</span>
                  <StatusPill status={selected.status || "new"} />
                </div>
                <div className="pt-3">
                  <p className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-1">Message</p>
                  <p className="text-slate-800 whitespace-pre-line bg-slate-50 rounded-lg p-3 border border-slate-100">
                    {selected.message || "No message body"}
                  </p>
                </div>
                {sentiment && (
                  <div className="pt-3 border-t border-slate-100">
                    <p className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-1">Sentiment</p>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold capitalize">{sentiment.label || "neutral"}</span>
                      <span className="text-xs text-slate-500">score: {sentiment.score}</span>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-slate-500">Select a message to see details and sentiment.</p>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminMessages;
